import { Button, Flex, FormControl, HStack, Stack, Text, useDisclosure, VStack } from "@chakra-ui/react";
import { useGetBuyQuotesMutation, useGetQuoteConfigQuery, useTransferInMutation } from "apis/swap";
import { CircularLoader, CryptoTxInput, ExchangeInfoAccordion, ExchangeInfoDetail, Input, InputLabel, SwapInfo } from "components";
import FrancoBankDetails from "components/Modal/FrancoBankDetails";
import configs from "config";
import { add } from "date-fns";
import { useCountry, useForm, usePartialState, useQuoteRate, useRedebounce } from "hooks";
import { IQuote, IQuoteRo, PageProps } from "interfaces";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  addCommas,
  currencyFormat,
  getCoinPrecision,
  isFiat,
  isValidCryptoAddress,
  MathRound,
  removeComma,
  toCoinLocale,
  toPrecision,
  when,
} from "utils";
import ls from "utils/secureStorage";
import { toUpperTrim } from "utils/textFormatter";

interface BuyPanelProps extends PageProps {
  authToken: string;
}

export function BuyPanel({ authToken }: BuyPanelProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const redebounce = useRedebounce();
  const { currency } = useCountry();

  const previewQuote = useRef(true);
  const lastPersisted = useRef(false);

  const kycRequired = useRef(ls.get(configs.KYC_REQUIRED));
  const accessKey = useRef(ls.get(configs.ACCESS_KEY));

  const sync = useRef(true);
  const qResRef = useRef<IQuoteRo | null>(null);
  const initTime = useRef(new Date());

  const { quoteRes: qRes, setBuyQuote, setQuoteRes, isActiveQuote, setTransferId, setTransferData } = useForm();
  const [transferIn, { isLoading: transferLoading }] = useTransferInMutation();

  const [isPersistingQuote, setIsPersistingQuote] = useState(false);
  const isPersistingQuoteRef = useRef(false);

  const queries = useMemo(() => new URLSearchParams(location?.search), [location]);

  const tokens = useMemo(() => {
    return Object.keys(configs.coins).filter((tk) => !isFiat(tk) && !["tbtc", "teth"].includes(tk));
  }, []);

  const coinToFiatRate = useRef<number>(0);
  const [quoteRes, setRes] = useState<IQuoteRo | null>(qRes);

  const [state, set] = usePartialState<IQuote>({
    fiatType: when(queries.has("fiat") && isFiat(queries.get("fiat")!), queries.get("fiat")?.toLowerCase()!, currency),
    cryptoType: when(
      queries.has("coin") && tokens.includes(queries.get("coin")?.toLowerCase()!),
      queries.get("coin")!,
      configs.DEFAULT_COIN
    ),
    address: when(queries.has("address"), queries.get("address")!, undefined),
    network: when(queries.has("network"), queries.get("network")!, undefined),
    memo: when(queries.has("memo"), queries.get("memo")!, undefined),
    tagNumber: when(queries.has("tagNumber"), queries.get("tagNumber")!, undefined),
    fiatAmount: when(queries.has("fiatAmount"), queries.get("fiatAmount")!, undefined),
    cryptoAmount: when(queries.has("cryptoAmount"), queries.get("cryptoAmount")!, undefined),
  });

  const { data: configData, isLoading: configLoading } = useGetQuoteConfigQuery(
    {
      cryptoType: state.cryptoType!,
      fiatType: state.fiatType!,
      type: "buy",
    },
    { skip: !accessKey.current }
  );

  const { isOpen } = useDisclosure();

  useEffect(() => {
    if (!configLoading) {
      if (state?.fiatAmount && Number(state?.fiatAmount) > 0) {
        handleFiatAmount(String(state.fiatAmount));
      } else if (state?.cryptoAmount && Number(state?.cryptoAmount) > 0) {
        handleCryptoAmount(String(state.cryptoAmount));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoading]);

  const { rate, isLoading: isRateLoading } = useQuoteRate("buy", state?.cryptoType!, state?.fiatType!, {
    cryptoAmount: Number(state.cryptoAmount ?? 0),
    fiatAmount: Number(state.fiatAmount ?? 0),
  });
  const [_buyCryptoRate, { isLoading: isLoadingRate }] = useGetBuyQuotesMutation();

  const showFranco = configs.francoCoins.includes(state.fiatType!);
  const francoCheck = useMemo(() => {
    return !!(state?.mobileNumber && state?.country && state?.bankName);
  }, [state]);

  const checkBuyRate = useCallback(
    async (
      price: number,
      opts?: {
        fiat?: string;
        cryptoAmount?: string;
        coin?: string;
        addr?: string;
        memo?: string;
        tagNumber?: string;
        network?: string;
        persist?: boolean;
        mobileNumber?: string;
        bankName?: string;
        country?: string;
      }
    ) => {
      const { address, memo, tagNumber, network, cryptoType, fiatAmount, fiatType } = state;

      try {
        const isPreview = previewQuote.current || !(address || opts?.addr);
        if (!isPreview) {
          isPersistingQuoteRef.current = true;
          setIsPersistingQuote(true);
        }

        const results = await _buyCryptoRate({
          body: {
            cryptoType: String(opts?.coin ? opts?.coin : cryptoType),
            fiatType: String(opts?.fiat ? opts?.fiat : fiatType),
            ...(opts?.cryptoAmount
              ? {
                  cryptoAmount: String(opts?.cryptoAmount),
                }
              : { fiatAmount: String(price ? price : fiatAmount) }),
            address: String(opts?.addr ? opts?.addr : address || ""),
            network: String(opts?.network ? opts?.network : network || ""),
            memo: String(opts?.memo ? opts?.memo : memo || ""),
            tagNumber: String(opts?.tagNumber ? opts?.tagNumber : tagNumber || ""),
            preview: isPreview,
            country: showFranco && !state.country ? "cameroon" : state?.country,
            bankName: state?.bankName,
            mobileNumber: state?.mobileNumber,
          },
          token: authToken || "",
          type: "in",
        }).unwrap();

        previewQuote.current = true;

        if (!!results) {
          setRes(results);

          coinToFiatRate.current = +(price ?? 0) / +(results?.quote?.cryptoAmount ?? 1);

          set({ cryptoAmount: results?.quote?.cryptoAmount, fiatAmount: results?.quote?.fiatAmount });
          setQuoteRes(results);

          const quoteId = results.quote.quoteId || "";
          ls.set(configs.QUOTE_ID, quoteId);
          kycRequired.current = results?.quote?.kyc?.kycRequired;
          ls.set(configs.KYC_REQUIRED, kycRequired.current);

          // use for internal state, cos state within redebounce lags behind by 1 frame
          qResRef.current = results;
          // our own home-built async await, when this value is triggered, some wait block is released
          // so it is important that the result is attached to qResRef by setting this value
          lastPersisted.current = !isPreview;

          const offset = 2; // secs offset
          initTime.current = add(new Date(), { seconds: Number(results.quote.duration) + offset });
          sync.current = true;
        } else setRes(null);

        isPersistingQuoteRef.current = false;
        setIsPersistingQuote(false);
      } catch (error) {
        setIsPersistingQuote(false);
        isPersistingQuoteRef.current = false;
        console.log("CheckRate Error", error);
        sync.current = false;
      }
    },
    [_buyCryptoRate, authToken, set, setQuoteRes, showFranco, state]
  );

  const createTransfer = useCallback(
    async (quoteId: string) => {
      try {
        if (quoteId && authToken) {
          const transferResult = await transferIn({ quoteId, token: authToken, type: "in" }).unwrap();
          setTransferId(transferResult?.transferId);
          setTransferData(transferResult);
          navigate("/payment");
          // navigate(`/payment/${transferResult?.transferId}`, { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      } catch (err: any) {
        if (String(err?.data?.error).toLocaleLowerCase() === "accountnamerequired") {
          navigate("/account-name");
        } else {
          navigate("/", { replace: true });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authToken]
  );

  // const flow = queries.get("flow");
  const proceedToNext = useCallback(async () => {
    setBuyQuote(state);
    const qRes = qResRef.current;
    if (!authToken || qRes?.loginRequired) {
      navigate("/checkout");
      return;
    }

    if ((authToken && kycRequired.current) || qRes?.kyc?.kycRequired) {
      navigate("/kyc");
      return;
    }

    if (authToken && !kycRequired.current && qRes?.quote?.quoteId) {
      await createTransfer(qRes?.quote?.quoteId);
      // navigate("/payment");
    }
  }, [authToken, createTransfer, navigate, setBuyQuote, state]);

  const validateCryptoAmount = useCallback(
    (value: number) => Number(value) >= configData?.minCryptoAmount! && Number(value) <= configData?.maxCryptoAmount!,
    [configData?.maxCryptoAmount, configData?.minCryptoAmount]
  );

  const validateFiatAmount = useCallback(
    (value: number) => {
      // return Number(value) >= configData?.minFiatAmount!;
      return Number(value) >= configData?.minFiatAmount! && Number(value) <= configData?.maxFiatAmount!;
    },
    [configData?.maxFiatAmount, configData?.minFiatAmount]
  );

  const wrapFiatCondition = useCallback(
    (value: number) => {
      const condition =
        (validateFiatAmount(Number(value)) && !showFranco) || (validateFiatAmount(Number(value)) && showFranco && francoCheck);
      return condition;
    },
    [francoCheck, showFranco, validateFiatAmount]
  );

  const handleFiat = (value: string) => {
    set({ fiatType: value as any });
    if (+(state?.fiatAmount ?? 0) <= 0 || !state?.address) return;
    if (wrapFiatCondition(Number(state.fiatAmount))) {
      // if (validateFiatAmount(Number(state.fiatAmount))) {
      redebounce(() => checkBuyRate(+(state?.fiatAmount ?? 0), { fiat: value }), "checkBuyRate", 1000)();
    }
  };

  const handleAddress = (value: string) => {
    set({ address: value as any });
    if (+(state?.fiatAmount ?? 0) <= 0 || value.length < 6) return;
    redebounce(() => checkBuyRate(+(state?.fiatAmount ?? 0), { addr: value }), "checkBuyRate", 1000)();
  };

  const handleTagNumber = (value: string) => {
    set({ tagNumber: value as any });
    if (+(state?.fiatAmount ?? 0) <= 0 || (state?.address && state?.address?.length < 6)) return;
    if (wrapFiatCondition(Number(state.fiatAmount)))
      redebounce(() => checkBuyRate(+(state?.fiatAmount ?? 0), { addr: state?.address }), "checkBuyRate", 1000)();
  };

  const handleMemo = (value: string) => {
    set({ memo: value as any });
    if (+(state?.fiatAmount ?? 0) <= 0 || (state?.address && state?.address?.length < 6)) return;
    if (wrapFiatCondition(Number(state.fiatAmount)))
      redebounce(() => checkBuyRate(+(state?.fiatAmount ?? 0), { addr: state?.address }), "checkBuyRate", 1000)();
  };

  const handleCrypto = (coin: string, network?: string) => {
    set({ cryptoType: coin as any, network });
    if (+(state?.fiatAmount ?? 0) <= 0) return;
    if (wrapFiatCondition(Number(state.fiatAmount)))
      // if (validateFiatAmount(Number(state.fiatAmount)))
      redebounce(() => checkBuyRate(+(state?.fiatAmount ?? 0), { coin, network }), "checkBuyRate", 1000)();
  };

  const handleFiatAmount = useCallback(
    (value: string) => {
      const newValue = value;
      set({ fiatAmount: newValue });
      if (+(newValue ?? 0) <= 0) return;

      if (wrapFiatCondition(Number(value))) {
        redebounce(() => checkBuyRate(+(newValue ?? 0), { fiat: state?.fiatType }), "checkBuyRate", 1000)();
      }

      if (Number(value) < configData?.minFiatAmount! || Number(value) > configData?.maxFiatAmount!) set({ cryptoAmount: "" });
    },
    [set, redebounce, configData?.minFiatAmount, configData?.maxFiatAmount, checkBuyRate, state?.fiatType, wrapFiatCondition]
  );

  const refetchQuote = useCallback(
    async (value: string) => {
      const newValue = removeComma(value);
      set({ fiatAmount: newValue });
      if (+(newValue ?? 0) <= 0) return;
      await checkBuyRate(+(newValue ?? 0), { fiat: state?.fiatType });
    },
    [checkBuyRate, set, state?.fiatType]
  );

  const handleCryptoAmount = (value: string) => {
    const updatedValue = removeComma(value);
    set({ cryptoAmount: updatedValue });
    // if (+(state?.fiatAmount ?? 0) <= 0) return;
    if (+(updatedValue ?? 0) <= 0) return;

    if (validateCryptoAmount(Number(value)))
      redebounce(() => checkBuyRate(+(state?.fiatAmount ?? 0), { cryptoAmount: updatedValue }), "checkBuyRate", 1000)();

    if (Number(value) < 5) set({ fiatAmount: "" });
  };

  const isValidAddress = useMemo(() => {
    return isValidCryptoAddress({
      address: state?.address!,
      coin: state?.cryptoType!,
      network: state?.network,
      tagNumber: state?.tagNumber,
      memo: state?.tagNumber,
    });
  }, [state]);

  const persistQuote = useCallback(
    async (e?: any) => {
      if (e) e.preventDefault();
      if (!isValidAddress) return false;

      if (lastPersisted.current && isActiveQuote()) {
        // will not make new request, but will let the function to proceed with existing quote
        return true;
      }

      if ((state.fiatAmount || state.cryptoAmount) && state.address && state.cryptoType && state.fiatType) {
        previewQuote.current = false;
        await refetchQuote(String(state.fiatAmount));
      }
      return true;
    },
    [isActiveQuote, refetchQuote, state, isValidAddress]
  );

  const formattedCryptoType = useMemo(() => {
    return toCoinLocale(state?.cryptoType! || "");
  }, [state.cryptoType]);

  const amountGotten = useMemo(() => {
    const cryptoAmount = quoteRes?.quote?.cryptoAmount ?? 0;
    const cryptoAmountMinusFees =
      Number(cryptoAmount) - Number(quoteRes?.quote?.processingFee ?? 0) - Number(quoteRes?.quote?.networkFee ?? 0);
    const roundedAmount = MathRound(cryptoAmountMinusFees, getCoinPrecision(state?.cryptoType || "", 12));

    const formatCryptoAmount = `${toPrecision(roundedAmount, state?.cryptoType!, false)}`;
    const formatFiatAmount = currencyFormat((state?.fiatType as any) ?? currency).format(+(quoteRes?.quote?.fiatAmount ?? 0));

    return `You get ${addCommas(formatCryptoAmount)} ${formattedCryptoType} for ${formatFiatAmount}`;
  }, [quoteRes, state, currency, formattedCryptoType]);

  const isLocked = queries.get("locked");

  const allParameters = useMemo(() => {
    return queries.has("coin") && queries.has("fiat") && queries.has("address");
  }, [queries]);

  const isDisabled = useMemo(
    () =>
      !(state?.fiatAmount && state?.fiatType && state?.cryptoType && state?.cryptoAmount && state?.address) ||
      isLoadingRate ||
      (Number(state?.fiatAmount) < configData?.minFiatAmount! && !(isLocked === "true" || allParameters)) ||
      (Number(state?.fiatAmount) < configData?.minFiatAmount! && !(isLocked === null)) ||
      (Number(state?.fiatAmount) > configData?.maxFiatAmount! && !(isLocked === "true" || allParameters)) ||
      // crypto
      (Number(state?.cryptoAmount) < configData?.minCryptoAmount! && !(isLocked === "true" || allParameters)) ||
      (Number(state?.cryptoAmount) > configData?.maxCryptoAmount! && !(isLocked === "true" || allParameters)) ||
      (!!state?.cryptoType && state?.cryptoType === "xrp" && !state?.tagNumber) ||
      (!!state?.cryptoType && state?.cryptoType === "xlm" && !state?.memo) ||
      !quoteRes ||
      !isValidAddress,
    [
      state?.fiatAmount,
      state?.fiatType,
      state?.cryptoType,
      state?.cryptoAmount,
      state?.address,
      state?.tagNumber,
      state?.memo,
      isLoadingRate,
      configData,
      isLocked,
      quoteRes,
      isValidAddress,
      allParameters,
    ]
  );

  const coinPassedInQuery = queries.has("coin");

  const disableCoinAddress = useMemo(() => {
    return (
      (queries.has("coin") && queries.has("address") && queries.get("locked") === "true") ||
      (queries.has("coin") && queries.has("address") && queries.has("fiat"))
    );
  }, [queries]);

  const actualPrice = useMemo(() => {
    return Number(state.cryptoAmount ?? 0) * Number(rate?.fiatAmount ?? 0);
  }, [rate?.fiatAmount, state.cryptoAmount]);

  return (
    <Stack
      as="form"
      my={{ base: "12px", md: "24px" }}
      py={{ base: "0", "2sm": "10px" }}
      onSubmit={async (e) => {
        const URL = location.search;
        ls.set(configs.PERSIST_URL, URL);
        await persistQuote(e);

        await proceedToNext();
      }}
      minH="60vh"
    >
      <FormControl mb={{ base: "10px !important", md: "20px !important" }}>
        <Flex justifyContent="space-between">
          <InputLabel htmlFor="amount" isLoading={isLoadingRate}>
            Amount in {toUpperTrim(state.fiatType || "Fiat")}
          </InputLabel>

          <InputLabel fontSize="11px" color="green.500" _text={{ fontWeight: "700" }}>
            {configLoading && <CircularLoader />}
            {!configLoading && Number(configData?.depositFee) > 0 && (
              <>
                {configData?.depositFee! > 0 ? "Deposit fee may apply" : ""}
                {/* {state?.fiatType?.toLowerCase() === "ngn" ? <>+150 to 1,000 </> : <>+{configData?.depositFee} </>}
                <Text as="span" textTransform="uppercase">
                  {state.fiatType}{" "}
                </Text>
                deposit fee */}
              </>
            )}
          </InputLabel>
        </Flex>

        <CryptoTxInput
          id="amount"
          placeholder="Enter Amount"
          value={state.fiatAmount ?? ""}
          defaultValue={state.fiatAmount ?? ""}
          type="number"
          coinValue={state?.fiatType ?? ""}
          onChange={(e) => handleFiatAmount(e.target.value)}
          pattern="[\d]*"
          formNoValidate
          inputMode="numeric"
          onCoinChange={handleFiat}
          _select={{ borderColor: "transparent" }}
          walletType={configs.fiats}
          selectTitle="Which currency do you want to pay with?"
          disableSelect={disableCoinAddress && queries.has("fiat")}
          // onSelect={() => {}}
        >
          {(configs.fiats ?? [])?.map((fiat) => (
            <option key={`buy-crypto-${fiat}`} value={fiat}>
              {toCoinLocale(fiat)}
            </option>
          ))}
        </CryptoTxInput>

        {state.fiatAmount && configData ? (
          <>
            {String(state.fiatAmount).length > 0 && Number(state.fiatAmount) < Number(configData?.minFiatAmount) && (
              <Text mt="4px" color="accent.500" fontWeight="500" fontSize="14px">
                Amount is too low. Minimum is {configData?.minFiatAmount.toLocaleString()} {String(state.fiatType).toUpperCase()}
              </Text>
            )}

            {String(state.fiatAmount).length > 0 && Number(state.fiatAmount) > Number(configData?.maxFiatAmount) && (
              <Text mt="4px" color="accent.500" fontWeight="500" fontSize="14px">
                Amount is too high. Maximum is {configData?.maxFiatAmount.toLocaleString()} {String(state.fiatType).toUpperCase()}
              </Text>
            )}
          </>
        ) : (
          <Text mt="4px" color="black.400" fontWeight="500" fontSize="14px">
            Minimum fiat amount is {configData?.minFiatAmount.toLocaleString()} {String(state.fiatType).toUpperCase()}
          </Text>
        )}
      </FormControl>

      {disableCoinAddress ? (
        <></>
      ) : (
        <FormControl mb={{ base: "10px !important", md: "20px !important" }}>
          <InputLabel htmlFor="volume">Volume in {toCoinLocale(state.cryptoType!) || "Token"}</InputLabel>
          <CryptoTxInput
            id="volume"
            placeholder="Equivalent Volume"
            type="number"
            pattern="[\d]*"
            formNoValidate
            value={state.cryptoAmount ?? ""}
            defaultValue={state.cryptoAmount ?? ""}
            coinValue={state?.cryptoType ?? ""}
            network={state?.network ?? ""}
            inputMode="numeric"
            onChange={(e) => handleCryptoAmount(e.target.value)}
            onCoinChange={handleCrypto}
            _select={{ borderColor: "transparent" }}
            disableSelect={coinPassedInQuery || disableCoinAddress}
            walletType={tokens}
            selectTitle="Which crypto do you want to buy?"
            isNetworkRequired
          >
            {(tokens ?? [])?.map((token) => (
              <option key={`buy-crypto-${token}`} value={token}>
                {toCoinLocale(token)}
              </option>
            ))}
          </CryptoTxInput>

          {state.cryptoAmount && configData && (
            <>
              {state.cryptoAmount && Number(state.cryptoAmount) < Number(configData?.minCryptoAmount) && (
                <Text mt="4px" color="accent.500" fontWeight="500" fontSize="14px">
                  Amount is too low. Minimum is {configData?.minCryptoAmount.toLocaleString()}{" "}
                  {String(state.cryptoType).toUpperCase()}
                </Text>
              )}

              {state.cryptoAmount && Number(state.cryptoAmount) > Number(configData?.maxCryptoAmount) && (
                <Text mt="4px" color="accent.500" fontWeight="500" fontSize="14px">
                  Amount is too high. Maximum is {configData?.maxCryptoAmount.toLocaleString()}{" "}
                  {String(state.cryptoType).toUpperCase()}
                </Text>
              )}
            </>
          )}
        </FormControl>
      )}

      {showFranco && (
        <FrancoBankDetails
          buyCheck={wrapFiatCondition(Number(state?.fiatAmount))}
          state={state}
          set={set}
          checkBuyRate={checkBuyRate}
        />
      )}

      <HStack>
        {!disableCoinAddress && (
          <>
            <FormControl flex={when(state?.cryptoType === "xrp" || state?.cryptoType === "xlm", "75%", "100%")}>
              <InputLabel>Address</InputLabel>
              <Input
                isRequired
                type="text"
                disabled={queries.has("address")}
                value={state?.address ?? ""}
                onChange={(e) => handleAddress(e.target.value)}
              />
            </FormControl>

            {!!state?.cryptoType && state?.cryptoType === "xlm" && (
              <FormControl
                flex="25%"
                // mb={{ base: "10px !important", md: "20px !important" }}
              >
                <InputLabel>Memo</InputLabel>
                <Input
                  isRequired
                  type="text"
                  disabled={queries.has("memo")}
                  value={state?.memo ?? ""}
                  onChange={(e) => handleMemo(e.target.value)}
                />
              </FormControl>
            )}

            {!!state?.cryptoType && state?.cryptoType === "xrp" && (
              <FormControl
                flex="25%"
                // mb={{ base: "10px !important", md: "20px !important" }}
              >
                <InputLabel>Tag Number</InputLabel>
                <Input
                  isRequired
                  type="text"
                  disabled={queries.has("tagNumber")}
                  value={state?.tagNumber ?? ""}
                  onChange={(e) => handleTagNumber(e.target.value)}
                />
              </FormControl>
            )}
          </>
        )}
      </HStack>
      <Text fontSize="14px" color="#ED1C00" as="span">
        {!!state.address && !isValidAddress && "Address is invalid"}
      </Text>

      <SwapInfo isLoading={isRateLoading}>
        1{" "}
        <Text as="span" textTransform="initial">
          {formattedCryptoType}
        </Text>{" "}
        = {rate?.fiatAmount ? currencyFormat((state?.fiatType as any) ?? currency).format(Number(rate?.fiatAmount) ?? 0) : ""}
      </SwapInfo>

      {!!quoteRes && validateFiatAmount(Number(state.fiatAmount)) && (
        <>
          <ExchangeInfoAccordion title={amountGotten} mt={{ base: "16px", md: "24px" }} isOpen={false}>
            <ExchangeInfoDetail
              title={`${toPrecision(+(state?.cryptoAmount ?? 0), state?.cryptoType ?? configs.DEFAULT_COIN)} @ ${currencyFormat(
                (state?.fiatType as any) ?? currency
              ).format(Number(rate?.fiatAmount) ?? 0)}`}
              description={currencyFormat((state?.fiatType as any) ?? currency).format(+(actualPrice ?? 0))}
            />

            {Number(quoteRes?.quote?.depositFee!) > 0 && (
              <ExchangeInfoDetail
                title="Deposit Fee"
                description={`${quoteRes?.quote.depositFee} ${quoteRes?.quote?.fiatType?.toUpperCase()}`}
              />
            )}

            <ExchangeInfoDetail
              title="Total Crypto Fee"
              description={`${quoteRes?.quote?.totalCryptoFee || 0} `.concat(formattedCryptoType)}
            />
          </ExchangeInfoAccordion>
        </>
      )}

      {!isOpen && (
        <VStack mt="34px !important">
          <Button
            w="100%"
            minW={{ base: "auto", "2sm": "400px" }}
            type="submit"
            isDisabled={isDisabled || isPersistingQuote || transferLoading}
            isLoading={isPersistingQuote || isPersistingQuoteRef.current || transferLoading}
            _hover={{
              _disabled: {
                opacity: 0.3,
              },
            }}
          >
            Continue
          </Button>
        </VStack>
      )}
    </Stack>
  );
}
